/**
 * ContratoBloco — Bloco F.8.
 *
 * Aparece no detalhe do orcamento APROVADO. Permite:
 *   - Listar contratos vinculados ao orcamento (PENDENTE/ASSINADO/RECUSADO)
 *   - Gerar novo contrato (escolhendo template do tipo CONTRATO)
 *   - Copiar link publico de contratos pendentes
 *   - Ver dados de assinatura (quando ASSINADO)
 *
 * Permissoes:
 *   - contrato:view para listar/ver
 *   - contrato:gerar para gerar novo
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  FiCheck, FiClock, FiCopy, FiExternalLink, FiFileText, FiPlus, FiX,
} from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  copiarLinkPublicoContrato,
  gerarContratoDeOrcamento,
  listarContratos,
} from "../../services/contratos";
import { listarTemplates } from "../../services/templatesOrcamento";

import { fmtData } from "./helpers";
import {
  Block,
  Field,
  FieldLabel,
  FieldValue,
  Form,
  FormFooter,
  IconButton,
  InlineHint,
  Pill,
  SectionTitle,
  Select,
} from "./styles";

const SITUACAO_TONE = {
  PENDENTE_ASSINATURA: "warning",
  ASSINADO: "success",
  RECUSADO: "neutral",
  EXPIRADO: "danger",
};

const SITUACAO_LABEL = {
  PENDENTE_ASSINATURA: "Pendente",
  ASSINADO: "Assinado",
  RECUSADO: "Recusado",
  EXPIRADO: "Expirado",
};

export default function ContratoBloco({ orcamento, onChange }) {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const toast = useToast();

  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateSel, setTemplateSel] = useState("");
  const [diasValidade, setDiasValidade] = useState(30);
  const [copiado, setCopiado] = useState(null);

  const podeVer = isMaster || hasAllPermissions(["contrato:view"]);
  const podeGerar = isMaster || hasAllPermissions(["contrato:gerar"]);

  const carregar = useCallback(async () => {
    if (!podeVer || !orcamento?.id) return;
    setLoading(true);
    try {
      const payload = await listarContratos(request, { orcamento: orcamento.id });
      const items = Array.isArray(payload) ? payload : payload?.results || [];
      setContratos(items);
    } catch (err) {
      // silencioso — auto-toast cobre
    } finally {
      setLoading(false);
    }
  }, [request, orcamento?.id, podeVer]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirGerar = async () => {
    setGerando(true);
    setTemplateSel("");
    setDiasValidade(30);
    // Carrega templates do tipo CONTRATO
    try {
      const payload = await listarTemplates(request, {
        tipo: "CONTRATO",
        ativo: true,
        pageSize: 50,
      });
      const items = Array.isArray(payload) ? payload : payload?.results || [];
      setTemplates(items);
      // Pre-seleciona primeiro template ativo (se houver)
      if (items.length > 0) {
        setTemplateSel(items[0].id);
      }
    } catch (err) {
      setTemplates([]);
    }
  };

  const handleGerar = async () => {
    const dias = Number(diasValidade) || 30;
    if (dias < 1 || dias > 365) {
      toast.error("Dias de validade deve estar entre 1 e 365.");
      return;
    }
    try {
      await gerarContratoDeOrcamento(request, orcamento.id, {
        templateId: templateSel || undefined,
        diasValidade: dias,
      });
      toast.success("Contrato gerado. Compartilhe o link com o cliente.");
      setGerando(false);
      await carregar();
      if (onChange) await onChange();
    } catch (err) {
      // auto-toast cobre
      throw err;
    }
  };

  // Security Sprint 0 / C2: link publico do contrato eh obtido via endpoint
  // dedicado (audita evento `contrato_link_copiado`). Anteriormente o token
  // vinha exposto em `c.token` na listagem — vazamento corrigido em 2026-05-21.
  const handleCopiarLink = async (contratoId) => {
    try {
      const { url } = await copiarLinkPublicoContrato(request, contratoId);
      if (navigator?.clipboard) {
        await navigator.clipboard.writeText(url);
        setCopiado(contratoId);
        setTimeout(() => setCopiado(null), 2000);
        toast.success("Link copiado.");
      } else {
        window.prompt("Copie o link:", url);
      }
    } catch (err) {
      toast.error(err?.message || "Erro ao obter o link.");
    }
  };

  const handleAbrirLink = async (contratoId) => {
    try {
      const { url } = await copiarLinkPublicoContrato(request, contratoId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.message || "Erro ao obter o link.");
    }
  };

  if (!podeVer) return null;

  return (
    <Block>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <SectionTitle>
          <FiFileText size={12} style={{ marginRight: 4 }} />
          Contratos ({contratos.length})
        </SectionTitle>
        {podeGerar && orcamento.situacao === "APROVADO" && (
          <AsyncButton
            variant="secondary"
            onClick={abrirGerar}
            silentSuccess
            silentError
          >
            <FiPlus size={12} /> Gerar contrato
          </AsyncButton>
        )}
      </div>

      {orcamento.situacao !== "APROVADO" && contratos.length === 0 && (
        <InlineHint>
          Contratos só podem ser gerados a partir de orçamento APROVADO.
        </InlineHint>
      )}

      {loading && contratos.length === 0 && (
        <InlineHint>Carregando contratos...</InlineHint>
      )}

      {contratos.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {contratos.map((c) => (
            <div
              key={c.id}
              style={{
                padding: 10,
                borderRadius: 8,
                border: "1px solid var(--border, #ddd)",
                background: "var(--surface, #fff)",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <strong style={{ fontSize: "0.92rem" }}>{c.numero}</strong>
                <Pill $tone={SITUACAO_TONE[c.situacao] || "neutral"}>
                  {c.situacao === "ASSINADO" && <FiCheck size={10} />}
                  {c.situacao === "RECUSADO" && <FiX size={10} />}
                  {c.situacao === "EXPIRADO" && <FiClock size={10} />}
                  {c.situacao === "PENDENTE_ASSINATURA" && <FiClock size={10} />}
                  {SITUACAO_LABEL[c.situacao] || c.situacao}
                </Pill>
              </div>

              {c.template_nome && (
                <FieldValue style={{ fontSize: "0.78rem", opacity: 0.85 }}>
                  Template: {c.template_nome}
                </FieldValue>
              )}

              {c.situacao === "PENDENTE_ASSINATURA" && c.utilizavel && (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <FieldLabel>Link público</FieldLabel>
                  <IconButton
                    type="button"
                    onClick={() => handleCopiarLink(c.id)}
                    title="Copiar link (chamada auditada)"
                  >
                    {copiado === c.id
                      ? <><FiCheck size={11} /> Copiado!</>
                      : <><FiCopy size={11} /> Copiar</>}
                  </IconButton>
                  <IconButton
                    type="button"
                    onClick={() => handleAbrirLink(c.id)}
                    title="Abrir em nova aba (chamada auditada)"
                  >
                    <FiExternalLink size={11} /> abrir
                  </IconButton>
                  <InlineHint>Valido ate {fmtData(c.expira_em)}</InlineHint>
                </div>
              )}

              {c.situacao === "ASSINADO" && (
                <FieldValue style={{ fontSize: "0.82rem" }}>
                  Assinado por <strong>{c.assinante_nome}</strong>
                  {c.assinante_email && ` <${c.assinante_email}>`}
                  {" "}em {fmtData(c.assinado_em)}
                  {c.pedido_numero && (
                    <> · Pedido <strong>{c.pedido_numero}</strong></>
                  )}
                </FieldValue>
              )}

              {c.situacao === "RECUSADO" && c.motivo_recusa && (
                <FieldValue style={{ fontSize: "0.82rem", color: "var(--danger, #9D2A22)" }}>
                  Motivo: {c.motivo_recusa}
                </FieldValue>
              )}
            </div>
          ))}
        </div>
      )}

      {/* SlideOverPanel — Gerar contrato */}
      <SlideOverPanel
        open={gerando}
        onClose={() => setGerando(false)}
        title="Gerar contrato"
        subtitle={orcamento ? `A partir do orçamento ${orcamento.numero}` : ""}
        width="min(95vw, 520px)"
      >
        <Form onSubmit={(e) => { e.preventDefault(); handleGerar(); }}>
          <Field>
            Template (opcional)
            <Select
              value={templateSel}
              onChange={(e) => setTemplateSel(e.target.value)}
            >
              <option value="">Padrão mínimo (embutido no sistema)</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}
                </option>
              ))}
            </Select>
            <InlineHint>
              {templates.length === 0
                ? "Nenhum template de contrato cadastrado. Cria em /comercial/templates (tipo Contrato) ou usa o padrão mínimo."
                : "Escolha um template HTML personalizado, ou deixe o padrão para usar o embutido."}
            </InlineHint>
          </Field>

          <Field>
            Validade do link público (dias)
            <input
              type="number"
              min="1"
              max="365"
              value={diasValidade}
              onChange={(e) => setDiasValidade(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid var(--border, #ddd)",
                borderRadius: 6,
                fontSize: "0.9rem",
              }}
            />
            <InlineHint>Após esta data, link público recusa assinatura.</InlineHint>
          </Field>

          <FormFooter>
            <AsyncButton
              variant="secondary"
              onClick={() => setGerando(false)}
              silentSuccess
              silentError
            >
              Cancelar
            </AsyncButton>
            <AsyncButton
              variant="primary"
              onClick={handleGerar}
              silentSuccess
              silentError
            >
              Gerar contrato
            </AsyncButton>
          </FormFooter>
        </Form>
      </SlideOverPanel>
    </Block>
  );
}
